// Session 58 Sprint A · /admin/audit-log · read-only viewer for admin_audit_log
// Filters: user, venue, workspace, action, date range. Paginated.
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: "", venue_id: "", workspace_id: "",
    action: "", date_from: "", date_to: "",
  });
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/admin/audit-log", { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally { setLoading(false); }
  }, [filters, offset]);

  useEffect(() => { reload(); }, [reload]);

  const clear = () => setFilters({ user_id:"", venue_id:"", workspace_id:"", action:"", date_from:"", date_to:"" });

  return (
    <div data-testid="admin-audit-log" style={{ background: "#FAF8F3", minHeight: "100vh",
                                                  padding: "32px 40px", fontFamily: "Georgia, serif",
                                                  color: "#1B2340" }}>
      <Toaster richColors position="top-center" />
      <div style={{ maxWidth: 1360, margin: "0 auto" }}>
        <Link to="/admin" style={{ fontSize: 12, color: "rgba(27,35,64,0.65)", textDecoration: "none" }}>← Admin</Link>
        <h1 style={{ fontSize: 34, fontWeight: 500, margin: "10px 0 4px",
                     fontFamily: "Cormorant Garamond, Georgia, serif" }}>
          Audit log
        </h1>
        <p style={{ fontSize: 13, color: "rgba(27,35,64,0.7)", margin: "0 0 20px" }}>
          Every mutation logged — team changes, revokes, role changes, and any admin write action.
        </p>

        <div style={filterPanel}>
          <FilterInput label="Actor user_id" value={filters.user_id}
                        onChange={v => setFilters(f => ({...f, user_id: v}))}
                        testid="audit-filter-user" />
          <FilterInput label="Venue id" value={filters.venue_id}
                        onChange={v => setFilters(f => ({...f, venue_id: v}))}
                        testid="audit-filter-venue" />
          <FilterInput label="Workspace id" value={filters.workspace_id}
                        onChange={v => setFilters(f => ({...f, workspace_id: v}))}
                        testid="audit-filter-workspace" />
          <FilterInput label="Action prefix" value={filters.action}
                        placeholder="team.assign, team.revoke…"
                        onChange={v => setFilters(f => ({...f, action: v}))}
                        testid="audit-filter-action" />
          <FilterInput label="From (ISO)" value={filters.date_from} type="date"
                        onChange={v => setFilters(f => ({...f, date_from: v}))}
                        testid="audit-filter-from" />
          <FilterInput label="To (ISO)" value={filters.date_to} type="date"
                        onChange={v => setFilters(f => ({...f, date_to: v}))}
                        testid="audit-filter-to" />
          <button type="button" onClick={clear} data-testid="audit-filter-clear"
                   style={btnGhost}>Clear filters</button>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: "rgba(27,35,64,0.65)" }}>
          {loading ? "Loading…" : `${total.toLocaleString()} entries · showing ${rows.length}`}
        </div>

        <div style={{ marginTop: 12, overflow: "auto", background: "#fff",
                       border: "1px solid rgba(27,35,64,0.08)", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
                  data-testid="audit-log-table">
            <thead>
              <tr style={{ textAlign: "left", background: "rgba(184,145,58,0.08)" }}>
                <th style={th}>When</th>
                <th style={th}>Who</th>
                <th style={th}>Action</th>
                <th style={th}>Target</th>
                <th style={th}>Meta (old → new)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }}
                    data-testid={`audit-row-${i}`}>
                  <td style={td}><span style={{fontFamily:"ui-monospace, monospace"}}>{fmtDate(r.at)}</span></td>
                  <td style={td}>
                    <div style={{fontWeight:600}}>{r.actor_email || r.actor_user_id || "system"}</div>
                    <div style={{fontSize:10,color:"rgba(27,35,64,0.55)",fontFamily:"ui-monospace, monospace"}}>{r.actor_user_id}</div>
                  </td>
                  <td style={td}>
                    <span style={actionPill(r.action)}>{r.action}</span>
                  </td>
                  <td style={td}>
                    {Object.entries(r.target || {}).map(([k, v]) => (
                      <div key={k} style={{fontFamily:"ui-monospace, monospace", fontSize:11}}>
                        <span style={{color:"rgba(27,35,64,0.55)"}}>{k}:</span> {String(v).slice(0, 60)}
                      </div>
                    ))}
                  </td>
                  <td style={td}>
                    <pre style={{margin:0,fontSize:10,color:"rgba(27,35,64,0.75)",whiteSpace:"pre-wrap",wordBreak:"break-all"}}>
                      {JSON.stringify(r.meta || {}, null, 0).slice(0, 320)}
                    </pre>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} style={{padding: 32, textAlign: "center", color: "rgba(27,35,64,0.55)"}}>
                  No audit entries match these filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <button type="button" onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                   disabled={offset === 0} data-testid="audit-prev"
                   style={{...btnGhost, opacity: offset === 0 ? 0.5 : 1}}>← Prev</button>
          <span style={{fontSize:12,color:"rgba(27,35,64,0.7)"}}>
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <button type="button" onClick={() => setOffset(offset + LIMIT)}
                   disabled={offset + LIMIT >= total} data-testid="audit-next"
                   style={{...btnGhost, opacity: offset + LIMIT >= total ? 0.5 : 1}}>Next →</button>
        </div>
      </div>
    </div>
  );
}

function FilterInput({label, value, onChange, testid, placeholder, type = "text"}) {
  return (
    <label style={{display:"flex", flexDirection:"column", gap:4, fontSize:11,
                    color:"rgba(27,35,64,0.7)", textTransform:"uppercase", letterSpacing:"0.08em"}}>
      {label}
      <input type={type} value={value} placeholder={placeholder || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              style={{padding:"7px 10px", background:"#FAF8F3", border:"1px solid rgba(27,35,64,0.15)",
                       borderRadius:8, fontSize:12, minWidth:180, fontFamily:"inherit"}} />
    </label>
  );
}

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, {
  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";

const actionPill = (action) => {
  const cat = (action || "").split(".")[0];
  const colors = {
    team: { bg: "#E4F1E8", fg: "#1B4634" },
    booking: { bg: "#E5EAF5", fg: "#243671" },
    stripe: { bg: "#F1EBDE", fg: "#5A410D" },
  }[cat] || { bg: "#EEE", fg: "#333" };
  return { display:"inline-block", padding:"3px 10px", background: colors.bg, color: colors.fg,
            fontSize:11, borderRadius:999, fontWeight:700, letterSpacing:"0.04em" };
};

const filterPanel = { display:"flex", gap:12, flexWrap:"wrap", padding:16, background:"#fff",
                       border:"1px solid rgba(27,35,64,0.08)", borderRadius:12, alignItems:"flex-end" };
const th = { padding:"10px 14px", fontSize:11, letterSpacing:"0.08em",
              textTransform:"uppercase", color:"rgba(27,35,64,0.65)", fontWeight:700 };
const td = { padding:"10px 14px", verticalAlign:"top" };
const btnGhost = { padding:"7px 14px", background:"#fff", color:"#1B2340",
                    border:"1px solid rgba(27,35,64,0.25)", borderRadius:999,
                    fontSize:12, fontWeight:600, cursor:"pointer" };
